import { supabase } from '../../supabase.js';
import { validateAndGetTenantUuid } from '../../utils/tenantUtils.js';
import { ServiceError } from '../../utils/errorHandler.js';

class AppSearchService {
  constructor() {
    this.appsTable = 'apps';
    this.reviewsTable = 'app_user_reviews';
    this.installationsTable = 'app_installations';
  }

  // Advanced search with multiple filters
  async advancedSearch(searchParams, tenantId) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      const {
        query = '',
        category = '',
        subcategory = '',
        gradeLevel = '',
        priceRange = null,
        rating = 0,
        sortBy = 'rating',
        sortOrder = 'desc',
        limit = 20,
        offset = 0,
        tags = [],
        developer = '',
        isFree = null,
        hasTrial = null
      } = searchParams;

      let queryBuilder = supabase
        .from(this.appsTable)
        .select(`
          *,
          app_developer:app_developers(
            developer_name,
            developer_email,
            verification_status
          ),
          reviews:app_user_reviews(count),
          installations:app_installations(count)
        `)
        .eq('tenant_id', tenantUuid)
        .eq('app_status', 'active');

      // Text search
      if (query.trim()) {
        queryBuilder = queryBuilder.or(
          `app_name.ilike.%${query}%,app_description.ilike.%${query}%,tags.cs.{${query}}`
        );
      }

      // Category filter
      if (category) {
        queryBuilder = queryBuilder.eq('app_category', category);
      }

      // Subcategory filter
      if (subcategory) {
        queryBuilder = queryBuilder.eq('app_subcategory', subcategory);
      }

      // Grade level filter
      if (gradeLevel) {
        queryBuilder = queryBuilder.contains('grade_levels', [gradeLevel]);
      }

      // Price range filter
      if (priceRange) {
        if (priceRange.min !== undefined) {
          queryBuilder = queryBuilder.gte('subscription_price', priceRange.min);
        }
        if (priceRange.max !== undefined) {
          queryBuilder = queryBuilder.lte('subscription_price', priceRange.max);
        }
      }

      // Rating filter
      if (rating > 0) {
        queryBuilder = queryBuilder.gte('rating', rating);
      }

      // Tags filter
      if (tags.length > 0) {
        queryBuilder = queryBuilder.overlaps('tags', tags);
      }

      // Developer filter
      if (developer) {
        queryBuilder = queryBuilder.eq('app_developer.developer_name', developer);
      }

      // Free/Paid filter
      if (isFree !== null) {
        queryBuilder = queryBuilder.eq('is_free', isFree);
      }

      // Trial filter
      if (hasTrial !== null) {
        queryBuilder = queryBuilder.eq('has_trial', hasTrial);
      }

      // Sorting
      const sortColumn = this.getSortColumn(sortBy);
      queryBuilder = queryBuilder.order(sortColumn, { ascending: sortOrder === 'asc' });

      // Pagination
      queryBuilder = queryBuilder.range(offset, offset + limit - 1);

      const { data, error, count } = await queryBuilder;

      if (error) {
        throw new ServiceError('SEARCH_FAILED', `Failed to search apps: ${error.message}`);
      }

      return {
        data: data || [],
        count: count || 0,
        pagination: {
          limit,
          offset,
          total: count || 0
        }
      };
    } catch (error) {
      if (error instanceof ServiceError) throw error;
      throw new ServiceError('SEARCH_FAILED', `Search operation failed: ${error.message}`);
    }
  }

  // Get app recommendations based on user preferences and history
  async getAppRecommendations(userId, tenantId, limit = 10) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      // Get user's installed apps and preferences
      const { data: userApps } = await supabase
        .from(this.installationsTable)
        .select('app_id, installed_at')
        .eq('user_id', userId)
        .eq('tenant_id', tenantUuid)
        .order('installed_at', { ascending: false })
        .limit(5);

      if (!userApps || userApps.length === 0) {
        // If no history, return trending apps
        return this.getTrendingApps(tenantId, limit);
      }

      // Get categories of user's installed apps
      const appIds = userApps.map(app => app.app_id);
      const { data: userAppCategories } = await supabase
        .from(this.appsTable)
        .select('app_category, app_subcategory, tags')
        .in('id', appIds);

      const categories = [...new Set(userAppCategories.map(app => app.app_category))];
      const tags = userAppCategories.flatMap(app => app.tags || []);

      // Find similar apps
      let queryBuilder = supabase
        .from(this.appsTable)
        .select(`
          *,
          app_developer:app_developers(
            developer_name,
            developer_email,
            verification_status
          ),
          reviews:app_user_reviews(count),
          installations:app_installations(count)
        `)
        .eq('tenant_id', tenantUuid)
        .eq('app_status', 'active')
        .not('id', 'in', `(${appIds.join(',')})`)
        .order('rating', { ascending: false })
        .limit(limit);

      // Filter by similar categories or tags
      if (categories.length > 0) {
        queryBuilder = queryBuilder.in('app_category', categories);
      }

      const { data, error } = await queryBuilder;

      if (error) {
        throw new ServiceError('RECOMMENDATIONS_FAILED', `Failed to get recommendations: ${error.message}`);
      }

      return {
        data: data || [],
        reason: 'Based on your installed apps'
      };
    } catch (error) {
      if (error instanceof ServiceError) throw error;
      throw new ServiceError('RECOMMENDATIONS_FAILED', `Recommendations failed: ${error.message}`);
    }
  }

  // Get trending apps based on installations and ratings
  async getTrendingApps(tenantId, limit = 10) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      const { data, error } = await supabase
        .from(this.appsTable)
        .select(`
          *,
          app_developer:app_developers(
            developer_name,
            developer_email,
            verification_status
          ),
          reviews:app_user_reviews(count),
          installations:app_installations(count)
        `)
        .eq('tenant_id', tenantUuid)
        .eq('app_status', 'active')
        .gte('rating', 4.0)
        .order('total_downloads', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Supabase error in getTrendingApps:', error);
        throw new ServiceError('TRENDING_FAILED', `Failed to get trending apps: ${error.message}`);
      }

      return {
        data: data || [],
        reason: 'Popular apps with high ratings'
      };
    } catch (error) {
      console.error('Error in getTrendingApps:', error);
      if (error instanceof ServiceError) throw error;
      throw new ServiceError('TRENDING_FAILED', `Trending apps failed: ${error.message}`);
    }
  }

  // Get recently added apps
  async getRecentlyAddedApps(tenantId, limit = 10) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      const { data, error } = await supabase
        .from(this.appsTable)
        .select(`
          *,
          app_developer:app_developers(
            developer_name,
            developer_email,
            verification_status
          ),
          reviews:app_user_reviews(count),
          installations:app_installations(count)
        `)
        .eq('tenant_id', tenantUuid)
        .eq('app_status', 'active')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Supabase error in getRecentlyAddedApps:', error);
        throw new ServiceError('RECENT_FAILED', `Failed to get recent apps: ${error.message}`);
      }

      return {
        data: data || [],
        reason: 'Recently added to the store'
      };
    } catch (error) {
      console.error('Error in getRecentlyAddedApps:', error);
      if (error instanceof ServiceError) throw error;
      throw new ServiceError('RECENT_FAILED', `Recent apps failed: ${error.message}`);
    }
  }

  // Get search suggestions based on popular searches
  async getSearchSuggestions(query, tenantId, limit = 5) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      if (!query.trim()) return { data: [] };

      const { data, error } = await supabase
        .from(this.appsTable)
        .select('app_name, app_category')
        .eq('tenant_id', tenantUuid)
        .eq('app_status', 'active')
        .or(`app_name.ilike.%${query}%,app_category.ilike.%${query}%`)
        .order('total_downloads', { ascending: false })
        .limit(limit);

      if (error) {
        throw new ServiceError('SUGGESTIONS_FAILED', `Failed to get suggestions: ${error.message}`);
      }

      return {
        data: data || []
      };
    } catch (error) {
      if (error instanceof ServiceError) throw error;
      throw new ServiceError('SUGGESTIONS_FAILED', `Search suggestions failed: ${error.message}`);
    }
  }

  // Helper method to get sort column
  getSortColumn(sortBy) {
    const sortColumns = {
      'rating': 'rating',
      'downloads': 'total_downloads',
      'reviews': 'total_reviews',
      'name': 'app_name',
      'price': 'subscription_price',
      'date': 'created_at',
      'updated': 'updated_at'
    };
    return sortColumns[sortBy] || 'rating';
  }
}

export const appSearchService = new AppSearchService();
